"use client";

import { chordpro } from "@bandwith/lezer-chordpro";
import { themeColors } from "@bandwith/styles";
import { syntaxHighlighting } from "@codemirror/language";
import { EditorState, type Extension } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import CodeMirror from "@uiw/react-codemirror";
import { useMemo } from "react";
import { chordProHighlighting } from "./chordpro-theme";

/**
 * Bandwith-themed CodeMirror theme for read-only display
 *
 * Colors are theme variables (themeColors), so they follow the OS theme.
 */
const displayTheme = EditorView.theme({
  "&": {
    backgroundColor: themeColors.bgSecondary,
    color: themeColors.textSecondary,
    fontSize: "14px",
    fontFamily:
      '"JetBrains Mono Variable", ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  ".cm-content": {
    padding: "12px 12px",
    caretColor: "transparent",
  },
  ".cm-scroller": {
    overflow: "auto",
  },
  "&.cm-focused": {
    outline: "none",
  },
  ".cm-line": {
    padding: "0",
  },
  // Hide cursor in read-only mode
  ".cm-cursor": {
    display: "none",
  },
});

type Props = {
  children: string;
  className?: string;
};

/**
 * Syntax-highlighted ChordPro code display using CodeMirror.
 *
 * Uses the same lezer grammar and highlighting as ChordProEditor
 * but in a minimal, read-only configuration.
 */
export function ChordProHighlight({ children, className = "" }: Props) {
  const extensions = useMemo<Extension[]>(
    () => [
      chordpro(),
      syntaxHighlighting(chordProHighlighting),
      EditorView.lineWrapping,
      EditorState.readOnly.of(true),
      EditorView.editable.of(false),
    ],
    [],
  );

  return (
    <div
      className={`rounded-lg border border-border overflow-hidden ${className}`}
    >
      <CodeMirror
        value={children}
        theme={displayTheme}
        extensions={extensions}
        basicSetup={false}
        editable={false}
      />
    </div>
  );
}
